export interface UserSettingsResponse {
  isTestSchedulerEnabled?: boolean;
  isAdvertisingEnabled?: boolean;
  isSmsSubscriber?: boolean;
  isCommercialNewsSubscriber?: boolean;
  isRecurringPaymentsEnabled?: boolean;
  isRecurringPaymentsEnabledForFamily?: boolean;
  hasAgreedToTerms?: boolean;
}

export interface UserPremiumSubscriptionResponse {
  isActive?: boolean;
  isActiveTillEndOfYear?: boolean;
  endDate?: string;
}

export enum SubscriptionPaymentKind {
  Sms = 'sms',
  Card = 'card',
}

export enum UserSetting {
  Advertising = 'advertising',
  CommercialNews = 'commercialNews',
}

export enum SubscriptionState {
  Inactive = 'inactive',
  ExpiringSoon = 'expiring-soon',
}

export type SubscriptionBannerState =
  | { kind: SubscriptionState.Inactive }
  | { kind: SubscriptionState.ExpiringSoon; daysRemaining: number };

export interface UserClassResponse {
  id: number;
  name: string;
  level: number;
  isCurrent: boolean;
}

export interface UserStudyYearResponse {
  id: number;
  name: string;
  isCurrent: boolean;
  isNext: boolean;
  datePeriod?: { from?: string; to?: string };
}

export interface UserSchoolResponse {
  regionId?: number;
}

export enum ProfileStatus {
  NextStudyYear = 'next-study-year',
  PastStudyYear = 'past-study-year',
  InactiveProfile = 'inactive-profile',
}

export interface UserInfoResponse {
  firstName?: string;
  lastName?: string;
  personType?: PersonType;
  personTypeName?: string;
  personTypeId?: number;
  studyYearId?: number;
  userPublicId?: string;
  username?: string;
  emailAddress?: string;
  phoneNumber?: string;
  userSettings?: UserSettingsResponse;
  premiumSubscription?: UserPremiumSubscriptionResponse;
  studyYear?: UserStudyYearResponse;
  class?: UserClassResponse;
  school?: UserSchoolResponse;
}

export interface StudentJoinOfferResponse {
  userPublicId: string;
  firstName: string;
  lastName: string;
  className: string;
  schoolName: string;
}

export enum DeclineStudentJoinOfferReason {
  PendingStudent = 'pending-student',
  DoesntAttendThisSchool = 'doesnt-attend-this-school',
  Other = 'other',
}

export interface AddFamilyMemberRequest {
  personalIdentityNumber: string;
}

export interface AddFamilyMemberResponse {
  joinedStudents: unknown[];
  isStudentAlreadyInFamily: boolean;
}

export interface FamilyMemberResponse {
  personType: PersonType;
  personTypeName: string;
  userProfile: {
    userPublicId: string;
    phoneNumber?: string;
    emailAddress?: string;
  } | null;
}

export interface FamilySettingsResponse {
  familyMembers: FamilyMemberResponse[];
}

export interface FamilyMember {
  userPublicId: string | null;
  personType: PersonType;
  personTypeName: string;
  phoneNumber: string | null;
  emailAddress: string | null;
}

export interface AddPhoneContext {
  personType: PersonType;
  studentUserPublicId: string;
}

export enum PersonType {
  Mother = 'Mother',
  Father = 'Father',
  OtherResponsiblePerson = 'OtherResponsiblePerson',
  Student = 'Student',
}

export const PARENT_ROLES: ReadonlySet<PersonType> = new Set([
  PersonType.Mother,
  PersonType.Father,
  PersonType.OtherResponsiblePerson,
]);

export type NotificationSummary = UnseenEventsResponse;

interface NestedTotal {
  total: number;
}

export interface UnseenEventsResponse {
  evaluationCount: number;
  nonAttendanceCount: number;
  teacherMessagesCount: NestedTotal & {
    diaryAnnouncements: number;
    behaviorEntries: number;
  };
  mailMessageCount: number;
  surveyCount: number;
  videoMeetingCount: number;
  newsCount: NestedTotal;
}

export interface ProfileIdentity {
  profileId: string;
  classId: number;
}

interface ProfileUserResponse {
  firstName: string;
  lastName: string;
  className?: string;
  schoolName?: string;
  personType: PersonType;
  personTypeName: string;
  studentDescription: string;
}

interface ProfileNotificationsResponse {
  count: number;
  summary?: NotificationSummary;
}

export interface ProfileEntryResponse {
  identity: ProfileIdentity;
  user: ProfileUserResponse;
  notifications: ProfileNotificationsResponse;
}

export interface StudyYearResponse {
  name: string;
  isCurrent: boolean;
  isNext: boolean;
  notificationCount: number;
  profiles: ProfileEntryResponse[];
  inactiveProfiles?: ProfileEntryResponse[];
}

export interface UserProfilesResponse {
  studyYears: StudyYearResponse[];
  notificationCount: number;
}

export type SwitchProfileRequest = ProfileIdentity;

export interface Notice {
  id: number;
  text: string;
}
